import { useLayoutEffect, useRef, useState } from 'react'
import { buildCalendarCells, monthStart, toIsoDate } from '../calendarDates'
import Button from './Button'
import './DateRangePicker.css'

const FIELD_GAP = 8
const VIEWPORT_MARGIN = 8

function getDateRangePickerPlacement({ fieldTop, fieldBottom, calendarHeight, viewportTop = 0, viewportBottom }) {
  const spaceBelow = Math.max(0, Math.floor(viewportBottom - VIEWPORT_MARGIN - fieldBottom - FIELD_GAP))
  const spaceAbove = Math.max(0, Math.floor(fieldTop - viewportTop - VIEWPORT_MARGIN - FIELD_GAP))
  // Prefer below; if neither side fits, use the larger space and allow scrolling.
  const side = calendarHeight > spaceBelow && spaceAbove > spaceBelow ? 'above' : 'below'
  return { side, maxHeight: side === 'above' ? spaceAbove : spaceBelow }
}

export default function DateRangePicker({ anchorRef, start, end, month, onMonthChange, onPick, onClear }) {
  const popoverRef = useRef(null)
  const [placement, setPlacement] = useState(null)

  useLayoutEffect(() => {
    const anchor = anchorRef.current
    const popover = popoverRef.current
    if (!anchor || !popover) return undefined

    let frameId
    const viewport = window.visualViewport
    const updatePlacement = () => {
      const field = anchor.getBoundingClientRect()
      const viewportTop = viewport?.offsetTop ?? 0
      const viewportHeight = viewport?.height ?? document.documentElement.clientHeight
      // Measure the full calendar even while its visible height is constrained.
      const calendarHeight = popover.scrollHeight + popover.offsetHeight - popover.clientHeight
      const next = getDateRangePickerPlacement({
        fieldTop: field.top,
        fieldBottom: field.bottom,
        calendarHeight,
        viewportTop,
        viewportBottom: viewportTop + viewportHeight,
      })
      setPlacement((previous) => previous?.side === next.side && previous.maxHeight === next.maxHeight ? previous : next)
    }
    const scheduleUpdate = () => {
      window.cancelAnimationFrame(frameId)
      frameId = window.requestAnimationFrame(updatePlacement)
    }

    updatePlacement()
    const observer = new ResizeObserver(scheduleUpdate)
    observer.observe(anchor)
    observer.observe(popover)
    window.addEventListener('resize', scheduleUpdate)
    window.addEventListener('scroll', scheduleUpdate, true)
    viewport?.addEventListener('resize', scheduleUpdate)
    viewport?.addEventListener('scroll', scheduleUpdate)
    return () => {
      window.cancelAnimationFrame(frameId)
      observer.disconnect()
      window.removeEventListener('resize', scheduleUpdate)
      window.removeEventListener('scroll', scheduleUpdate, true)
      viewport?.removeEventListener('resize', scheduleUpdate)
      viewport?.removeEventListener('scroll', scheduleUpdate)
    }
  }, [anchorRef, month])

  const navigate = (months) => {
    onMonthChange(new Date(month.getFullYear(), month.getMonth() + months, 1))
  }

  const pickToday = () => {
    const today = new Date()
    onMonthChange(monthStart(today))
    onPick(toIsoDate(today))
  }

  return (
    <div
      ref={popoverRef}
      className="date-range-popover"
      data-placement={placement?.side || 'below'}
      style={placement ? { maxHeight: `${placement.maxHeight}px` } : undefined}
    >
      <div className="date-range-head">
        <div className="date-range-nav-group">
          <button className="date-range-nav-btn" type="button" onClick={() => navigate(-12)} aria-label="Ein Jahr zurück">«</button>
          <button className="date-range-nav-btn" type="button" onClick={() => navigate(-1)} aria-label="Ein Monat zurück">‹</button>
        </div>
        <div className="date-range-head-label">{month.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</div>
        <div className="date-range-nav-group">
          <button className="date-range-nav-btn" type="button" onClick={() => navigate(1)} aria-label="Ein Monat vor">›</button>
          <button className="date-range-nav-btn" type="button" onClick={() => navigate(12)} aria-label="Ein Jahr vor">»</button>
        </div>
      </div>
      <div className="date-range-weekdays">
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => <span key={day}>{day}</span>)}
      </div>
      <div className="date-range-grid">
        {buildCalendarCells(month).map((cell, index) => {
          const inRange = cell && start && end && cell >= start && cell <= end
          const isStart = cell && start === cell
          const isEnd = cell && end === cell
          return (
            <button
              key={cell || `empty-${index}`}
              className={`date-cell${cell ? '' : ' is-empty'}${inRange ? ' is-in-range' : ''}${isStart ? ' is-start' : ''}${isEnd ? ' is-end' : ''}`}
              type="button"
              disabled={!cell}
              onClick={() => onPick(cell)}
            >
              {cell ? Number(cell.slice(8, 10)) : ''}
            </button>
          )
        })}
      </div>
      <div className="date-range-actions">
        <Button variant="secondary" tone="destructive" onClick={onClear}>Leeren</Button>
        <Button variant="secondary" onClick={pickToday}>Heute</Button>
      </div>
    </div>
  )
}
